import React, { useState, useEffect, Fragment } from "react";
import PaymentGateway from "../PaymentGateway"
import { useAuth } from "../../context/AuthContext";
import { getPurohitProfile } from "../../services/api";
import { toast } from "react-hot-toast";
import { purohitSubscriptionYearly } from "../../services/api";


import { Crown, Camera, Check, Upload, X } from "lucide-react";
import {
  STATES,
  SPECIALIZATIONS,
  LANGUAGES,
  SERVICES,
  regex,
} from "./PurohitConfig";

const initialData = {
  // step 1
  fullName: "",
  email: "",
  phone: "",
  altPhone: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  profilePhoto: null,
  profilePhotoUrl: "",

  // step 2
  experienceYears: "",
  specializations: [],
  languages: [],

  // step 3
  biography: "",
  achievements: "",
  servicesOffered: [],
  // minPrice: "",
  // maxPrice: "",
};

const STEP_LABELS = ["Personal", "Expertise", "Services"];

const PurohitRegistration = ({ onClose }) => {
  const { getCurrentUser, hasPurohitProfile, updatePurohitProfile } = useAuth();

  const [step, setStep] = useState(1);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ensure scrolling is enabled when component mounts
  // Load from sessionStorage and pre-fill logged-in user's email
  useEffect(() => {
    // ✅ Check if profile is COMPLETE (not just exists)
    const currentUser = getCurrentUser();

    if (currentUser && currentUser.isComplete === true) {
      toast.error(
        "You already have a complete profile! Use 'Edit Profile' to make changes."
      );
      onClose();
      return;
    }

    // ✅ Load saved purohit data from sessionStorage
    try {
      const saved = sessionStorage.getItem("purohitData");
      const savedStep = sessionStorage.getItem("registrationStep");

      if (saved) {
        const parsed = JSON.parse(saved);
        setData((prev) => ({ ...prev, ...parsed }));

        if (parsed.profilePhotoUrl) {
          setPhotoPreview(parsed.profilePhotoUrl);
        }
      }

      if (savedStep) {
        setStep(Number(savedStep));
      }
    } catch (e) {
      console.error("Failed to load purohitData from sessionStorage", e);
    }

    // ✅ Pre-fill from Register page data (for new registrations)
    if (currentUser && currentUser.email) {
      setData((prev) => ({
        ...prev,
        email: currentUser.email || prev.email,
        fullName: currentUser.fullName || prev.fullName,
        phone: currentUser.phone || prev.phone,
        address: currentUser.address || prev.address,
        city: currentUser.city || prev.city,
        pincode: currentUser.pincode || currentUser.pinCode || prev.pincode,
      }));
    }
  }, [getCurrentUser, onClose]);

  // Sync to sessionStorage on any data / photo change
  useEffect(() => {
    try {
      const { profilePhoto, ...rest } = data;
      const payload = {
        ...rest,
        profilePhotoUrl: photoPreview || rest.profilePhotoUrl || "",
      };
      sessionStorage.setItem("purohitData", JSON.stringify(payload));
    } catch (e) {
      console.error("Failed to store purohitData in sessionStorage", e);
    }
  }, [data, photoPreview]);

  // Live validation per field
  const validateField = (name, value) => {
    let err = "";

    switch (name) {
      case "fullName":
        if (!regex.name.test(value))
          err = "Enter a valid name (letters only, min 3 chars, max 50 chars)";
        break;

      case "phone":
      case "altPhone":
        if (!regex.phone.test(value))
          err = "Enter a valid 10 digit mobile number";
        break;

      case "email":
        if (!regex.email.test(value) || value.length > 50)
          err = "Enter a valid email address (example:user@gmail.com)";
        break;

      case "address":
        if (value.length < 4) err = "Enter your full address(min 4)";
        break;

      case "city":
        if (!regex.name.test(value)) err = "Enter a valid city name";
        break;

      case "pincode":
        if (!regex.pincode.test(value)) err = "Enter a valid 6 digit pincode";
        break;

      case "experienceYears":
        if (!value || !regex.experience.test(value))
          err = "Enter valid years of experience";
        break;

      case "biography":
        if (!value || value.trim().length === 0) {
          err = "Please fill the biography field";
        } else if (value.length < 20) {
          err = "Enter at least 20 characters";
        } else if (value.length > 500) {
          err = "Biography should not exceed 500 characters";
        } else {
          err = "";
        }
        break;

      case "achievements":
        if (value.length < 20 && value.length > 0)
          err = "Enter at least 20 characters";
        if (value.length > 500)
          err = "Achievements should not exceed 500 characters";
        break;

      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: err }));
    return !err;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (["fullName", "city"].includes(name))
      value = value.replace(/[^A-Za-z\s]/g, "");

    if (["phone", "altPhone", "pincode", "experienceYears"].includes(name))
      value = value.replace(/\D/g, "");

    if (name === "fullName") value = value.slice(0, 50);
    if (name === "email") value = value.slice(0, 50);
    if (name === "phone" || name === "altPhone") value = value.slice(0, 10);
    // if (name === "address") value = value.slice(0, 100);

    if (name === "address") {
      // 1️⃣ Limit length to 100 characters
      value = value.slice(0, 100);

      // 2️⃣ Allow only letters, numbers, space, comma, dot, dash
      const addressRegex = /^[A-Za-z0-9\s,.-]*$/;

      if (!addressRegex.test(value)) {
        setErrors((prev) => ({
          ...prev,
          address:
            "Address can contain only letters, numbers, comma, dot and dash (-)",
        }));
        return; // ⛔ block invalid character
      }
    }

    if (name === "city") value = value.slice(0, 50);
    if (name === "pincode") value = value.slice(0, 6);
    if (name === "experienceYears") value = value.slice(0, 2);
    if (name === "biography" || name === "achievements")
      value = value.slice(0, 500);

    setData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleCheckboxChange = (name, value) => {
    setData((prev) => {
      const current = new Set(prev[name] || []);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [name]: Array.from(current) };
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpe?g|png|webp)$/i)) {
      setErrors((prev) => ({
        ...prev,
        profilePhoto: "Only JPG, JPEG, PNG, WEBP are allowed.",
      }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        profilePhoto: "File size should be less than 5MB.",
      }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoPreview(ev.target.result);
      setData((prev) => ({
        ...prev,
        profilePhoto: file,
        profilePhotoUrl: ev.target.result,
      }));
    };
    reader.readAsDataURL(file);
    setErrors((prev) => ({ ...prev, profilePhoto: "" }));
  };

  const validateStep = (currentStep) => {
    const newErrors = {};
    const d = data;

    if (currentStep === 1) {
      if (!d.profilePhoto && !d.profilePhotoUrl)
        newErrors.profilePhoto = "Profile photo is required.";
      if (!d.fullName || !regex.name.test(d.fullName))
        newErrors.fullName =
          "Enter a valid full name (only letters, min 3 characters max 50 character).";
      if (!d.email || !regex.email.test(d.email))
        newErrors.email = "Enter a valid email address.";
      if (!d.phone || !regex.phone.test(d.phone))
        newErrors.phone =
          "Enter a valid Indian mobile number (with or without +91).";
      if (d.altPhone && !regex.phone.test(d.altPhone))
        newErrors.altPhone = "Enter a valid 10 digit mobile number.";

      if (!d.address || d.address.trim().length < 5)
        newErrors.address = "Enter your full address.";
      if (!d.city || !regex.name.test(d.city))
        newErrors.city = "Enter a valid city name.";
      if (!d.state) newErrors.state = "State is required.";
      if (!d.pincode || !regex.pincode.test(d.pincode))
        newErrors.pincode = "Enter a valid 6-digit PIN code.";
    }

    if (currentStep === 2) {
      if (!d.experienceYears || !regex.experience.test(d.experienceYears))
        newErrors.experienceYears =
          "Enter valid years of experience (numeric).";
      if (!d.specializations.length)
        newErrors.specializations = "Select at least one specialization.";
      else if (d.specializations.length > 5)
        newErrors.specializations =
          "You can select only up to 5 specializations.";

      if (!d.languages.length)
        newErrors.languages = "Select at least one language.";
      else if (d.languages.length > 5)
        newErrors.languages = "You can select only up to 5 languages.";
    }

    if (currentStep === 3) {
      if (!d.biography) newErrors.biography = "Please fill the biography field";
      if (d.biography.trim().length < 20)
        newErrors.biography = "Biography must be at least 20 characters.";
      if (
        d.achievements &&
        d.achievements.trim().length < 20 &&
        d.achievements.trim().length > 0
      )
        newErrors.achievements =
          "Either leave empty or describe your achievements properly.";
      if (!d.servicesOffered.length)
        newErrors.servicesOffered = "Select at least one service.";
      else if(d.servicesOffered.length>5)
        newErrors.servicesOffered="you can select only up to 5 services"
      
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => {
    if (step === 3) {
      if (validateStep(3)) {
        setShowPayment(true);
      }
      return;
    }
    if (validateStep(step)) {
      setStep((s) => s + 1);
      sessionStorage.setItem("registrationStep", String(step + 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goBack = () => {
    if (step === 1) {
      // Close the form and go back to dashboard
      onClose();
      return;
    }
    setStep((s) => s - 1);
    sessionStorage.setItem("registrationStep", String(step - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePaymentSuccess = async () => {
    console.log("Payment successful! Submitting profile...");

    // Check if profile already exists (double-check)
    if (hasPurohitProfile()) {
      toast.error(
        "You already have a profile! Cannot create multiple profiles."
      );
      onClose();
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare profile data for API - ensure exact match with backend schema
      const profileData = {
        // Personal info
        fullName: data.fullName.trim(),
        email: data.email.toLowerCase().trim(),
        phone: data.phone.trim(),
        altPhone: data.altPhone ? data.altPhone.trim() : undefined,

        // Address info
        address: data.address.trim(),
        city: data.city.trim(),
        state: data.state,
        pincode: data.pincode.trim(),

        // Experience & skills
        experienceYears: Number(data.experienceYears),
        specializations: data.specializations || [],
        languages: data.languages || [],

        // Bio & services
        biography: data.biography.trim(),
        achievements: data.achievements ? data.achievements.trim() : undefined,
        servicesOffered: data.servicesOffered || [],

        // Pricing
        // minPrice: Number(data.minPrice),
        // maxPrice: Number(data.maxPrice),

        // Optional
        profilePhotoUrl: data.profilePhotoUrl || undefined,
        isComplete: true,
      };

      // Remove undefined fields to avoid validation issues
      Object.keys(profileData).forEach((key) => {
        if (profileData[key] === undefined) {
          delete profileData[key];
        }
      });

      // Debug logging
      console.log("=== PROFILE CREATION DEBUG ===");
      console.log("Profile Data:", JSON.stringify(profileData, null, 2));
      console.log("Auth exists:", !!localStorage.getItem("auth"));

      const authData = localStorage.getItem("auth");
      if (authData) {
        const parsed = JSON.parse(authData);
        console.log("Auth Info:", {
          hasToken: !!parsed.token,
          role: parsed.role,
          email: parsed.user?.email,
        });
      }
      console.log("=== END DEBUG ===");

      // Submit to API
      const response = await purohitProfile(profileData);

      console.log("Profile creation response:", response.data);

      // Update context with new profile
      updatePurohitProfile(response.data.profile || response.data);

      // Clear session storage
      sessionStorage.removeItem("purohitData");
      sessionStorage.removeItem("registrationStep");
      sessionStorage.removeItem("registrationPaymentCompleted");

      toast.success("Profile created successfully!", {
        duration: 3000,
      });

      // Close modal
      onClose();
    } catch (error) {
      console.error("Profile creation error:", error);
      console.error("Error response:", error.response);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create profile. Please try again.";

      toast.error(errorMessage, {
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentClose = () => {
    setShowPayment(false);
  };

  const stepStatus = (index) => {
    if (step === index) return "current";
    if (step > index) return "done";
    return "upcoming";
  };

  const StepCircle = ({ index }) => {
    const status = stepStatus(index);
    const base =
      "flex items-center justify-center w-10 h-10 rounded-full text-sm font-semibold border";
    let size = "w-8 h-8 text-xs";
    let classes = "";
    let content = index;

    if (status === "current") {
      classes =
        "bg-gradient-to-b from-orange-500 to-red-500 text-white border-transparent shadow-md";
    } else if (status === "done") {
      classes =
        "bg-gradient-to-b from-orange-500 to-red-500 text-white border-transparent";
      content = <Check size={14} strokeWidth={3} />;
    } else {
      classes = "bg-gray-100 text-gray-400 border-gray-200";
    }

    return (
      <div className="flex flex-col items-center gap-1">
        <div className={`${base} ${size} ${classes}`}>{content}</div>
        <span
          className={`text-[10px] sm:text-[12px] ${
            status === "upcoming" ? "text-gray-400" : "text-gray-700"
          }`}
        >
          {STEP_LABELS[index - 1]}
        </span>
      </div>
    );
  };
if (showPayment) {
  return (
    <PaymentGateway
      amount="999"
      userDetails={{
        name: data.fullName,
        email: data.email,
        phone: data.phone,
      }}
      paymentType="purohit_profile_creation"
      metadata={{
        purohitData: data,
      }}

      // 🔥 HERE IS WHERE YOU INTEGRATE THE API
      onPaymentSubmit={async (paymentData) => {
        try {
          await purohitSubscriptionyearly({
            paymentId: paymentData.paymentId,
            orderId: paymentData.orderId,
            amount: paymentData.amount,
            subscriptionType: "YEARLY",
          });
        } catch (err) {
          toast.error("Subscription activation failed");
          throw err; // ⛔ stops onSuccess
        }
      }}

      // ✅ Runs ONLY if payment + API succeed
      onSuccess={handlePaymentSuccess}

      onClose={handlePaymentClose}
      title="Complete Profile Creation"
      description="One-time payment to activate your purohit profile"
    />
  );
}


  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white mt-10 to-red-50">
      <div className="max-w-6xl mx-auto px-3 md:px-4 py-8 md:py-10">
        <form
          onSubmit={(e) => e.preventDefault()}
          className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.05)] border-2 border-orange-200 mt-12 px-5 sm:px-8 py-8 md:py-10 max-w-4xl mx-auto"
        >
          <div className="text-center mb-6 md:mb-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-b from-orange-500 to-red-500 flex items-center justify-center shadow-lg mb-3">
              <Crown size={32} strokeWidth={2.5} className="text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Purohit Registration
            </h1>
            <p className="text-gray-600">
              Join our platform and serve families worldwide
            </p>
          </div>

          <div className="w-full pb-2 mb-4">
            <div className="flex justify-center items-center gap-1 sm:gap-3 flex-nowrap w-full">
              {[1, 2, 3].map((i) => (
                <Fragment key={i}>
                  <StepCircle index={i} />
                  {i < 3 && (
                    <div
                      className={`h-1 w-6 sm:w-12 md:w-16 lg:w-20 rounded-full ${
                        step > i
                          ? "bg-gradient-to-r from-orange-500 to-red-500"
                          : "bg-gray-300"
                      }`}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          {/* STEP 1 – PERSONAL */}
          {step === 1 && (
            <>
              <h2 className="text-xl md:text-2xl font-semibold text-center text-gray-900">
                Personal Information
              </h2>
              <p className="text-center text-gray-500 mb-6">
                Tell us about yourself
              </p>

              {/* photo */}
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6 text-center">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-b from-orange-500 to-red-500 flex items-center justify-center text-3xl text-white shadow-md overflow-hidden">
                    {photoPreview && data.profilePhotoUrl ? (
                      <img
                        src={photoPreview}
                        alt="Profile"
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <Camera size={28} strokeWidth={2.5} />
                    )}
                  </div>
                  <label className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow cursor-pointer">
                    <Camera size={14} className="text-orange-500" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </label>
                </div>
                <div>
                  <div className="flex flex-wrap gap-3 mb-1 w-full text-center justify-center md:justify-start md:text-left">
                    <label className="inline-flex">
                      <span className="sr-only">Change photo</span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handlePhotoChange}
                      />
                      <span className="inline-flex items-center px-4 py-2 rounded-full border border-gray-200 text-sm font-medium cursor-pointer hover:bg-gray-50">
                        <Upload className="h-4 w-4 mr-2" />
                        Change Photo
                      </span>
                    </label>
                    {data.profilePhotoUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setData((p) => ({
                            ...p,
                            profilePhoto: null,
                            profilePhotoUrl: "",
                          }));
                          setPhotoPreview(null);
                        }}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-gray-200 text-sm font-medium hover:bg-gray-50"
                      >
                        <X size={16} className="text-red-500" />
                        Remove Photo
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    JPG, JPEG, PNG, WEBP — max 5MB. Preview will show before
                    saving.
                  </p>
                  {errors.profilePhoto && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.profilePhoto}
                    </p>
                  )}
                </div>
              </div>

              {/* name / phone */}
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="fullName"
                    value={data.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                      errors.fullName ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.fullName && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.fullName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="phone"
                    value={data.phone}
                    onChange={handleChange}
                    placeholder="9876543210"
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                      errors.phone ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>

              {/* email / alt phone */}
              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={data.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                      errors.email ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Alternate Phone
                  </label>
                  <input
                    name="altPhone"
                    value={data.altPhone}
                    onChange={handleChange}
                    placeholder="9876543211"
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                      errors.altPhone ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.altPhone && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.altPhone}
                    </p>
                  )}
                </div>
              </div>

              {/* address */}
              <div className="mt-5">
                <label className="block text-sm font-medium mb-1">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  name="address"
                  value={data.address}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Enter your full address"
                  className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                    errors.address ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {errors.address && (
                  <p className="text-xs text-red-500 mt-1">{errors.address}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="city"
                    value={data.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                      errors.city ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.city && (
                    <p className="text-xs text-red-500 mt-1">{errors.city}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="state"
                    value={data.state}
                    onChange={handleChange}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 bg-white ${
                      errors.state ? "border-red-400" : "border-gray-200"
                    }`}
                  >
                    <option value="">Select state</option>
                    {STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <p className="text-xs text-red-500 mt-1">{errors.state}</p>
                  )}
                </div>
              </div>

              <div className="mt-5 max-w-xs">
                <label className="block text-sm font-medium mb-1">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  name="pincode"
                  value={data.pincode}
                  onChange={handleChange}
                  placeholder="Enter pincode"
                  className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                    errors.pincode ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {errors.pincode && (
                  <p className="text-xs text-red-500 mt-1">{errors.pincode}</p>
                )}
              </div>
            </>
          )}

          {/* STEP 2 – EXPERTISE */}
          {step === 2 && (
            <>
              <h2 className="text-xl md:text-2xl font-semibold text-center text-gray-900">
                Qualifications &amp; Expertise
              </h2>
              <p className="text-center text-gray-500 mb-6">
                Share your professional background
              </p>

              <div className="space-y-5">
                <div className="max-w-xs">
                  <label className="block text-sm font-medium mb-1">
                    Years of Experience <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="experienceYears"
                    value={data.experienceYears}
                    onChange={handleChange}
                    placeholder="e.g., 15"
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                      errors.experienceYears
                        ? "border-red-400"
                        : "border-gray-200"
                    }`}
                  />
                  {errors.experienceYears && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.experienceYears}
                    </p>
                  )}
                </div>

                {/* Specializations */}
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <label className="text-sm font-medium">
                      Specializations <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    {SPECIALIZATIONS.map((spec) => (
                      <div
                        key={spec}
                        className="flex items-center gap-2 select-none"
                      >
                        <input
                          type="checkbox"
                          checked={data.specializations.includes(spec)}
                          onChange={() =>
                            handleCheckboxChange("specializations", spec)
                          }
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer"
                        />
                        <span className="text-gray-700 pointer-events-none">
                          {spec}
                        </span>
                      </div>
                    ))}
                  </div>
                  {errors.specializations && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.specializations}
                    </p>
                  )}
                </div>

                {/* Languages */}
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <label className="text-sm font-medium">
                      Languages Known <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2 text-sm">
                    {LANGUAGES.map((lang) => (
                      <div
                        key={lang}
                        className="flex items-center gap-2 select-none"
                      >
                        <input
                          type="checkbox"
                          checked={data.languages.includes(lang)}
                          onChange={() =>
                            handleCheckboxChange("languages", lang)
                          }
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer"
                        />
                        <span className="text-gray-700">{lang}</span>
                      </div>
                    ))}
                  </div>
                  {errors.languages && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.languages}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* STEP 3 – SERVICES */}
          {step === 3 && (
            <>
              <h2 className="text-xl md:text-2xl font-semibold text-center text-gray-900">
                Services &amp; Pricing
              </h2>
              <p className="text-center text-gray-500 mb-6">
                Define your offerings
              </p>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Biography <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="biography"
                    value={data.biography}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Write a brief introduction about yourself, your experience, and approach to spiritual services..."
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 resize-y max-h-32 resize-none ${
                      errors.biography ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.biography && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.biography}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Achievements &amp; Recognition
                  </label>
                  <textarea
                    name="achievements"
                    value={data.achievements}
                    onChange={handleChange}
                    rows={2}
                    placeholder="List your notable achievements, awards, and recognition..."
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 resize-y max-h-32 resize-none${
                      errors.achievements ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.achievements && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.achievements}
                    </p>
                  )}
                </div>

                {/* services offered */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Services Offered <span className="text-red-500">*</span>
                  </label>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    {SERVICES.map((srv) => (
                      <div
                        key={srv}
                        className="flex items-center gap-2 select-none"
                      >
                        <input
                          type="checkbox"
                          checked={data.servicesOffered.includes(srv)}
                          onChange={() =>
                            handleCheckboxChange("servicesOffered", srv)
                          }
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer"
                        />
                        <span className="text-gray-700">{srv}</span>
                      </div>
                    ))}
                  </div>
                  {errors.servicesOffered && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.servicesOffered}
                    </p>
                  )}
                </div>

              </div>
            </>
          )}

          {/* Buttons */}
          <div className="mt-8 flex items-center justify-between">
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              ← Back
            </button>

            <button
              type="button"
              onClick={goNext}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:opacity-95"
            >
              {step === 3 ? "Proceed to Payment" : "Next"} <span>→</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurohitRegistration;














